import { useEffect, useRef } from 'react'
import type { CSSProperties } from 'react'

// Premium gold-ring cursor accent that hugs the native cursor (we never set
// cursor:none, so the OS pointer/grab affordances survive). It follows tightly with
// a hint of smoothing and grows + fills over interactive targets. Disabled on
// coarse/touch pointers; snaps instantly under prefers-reduced-motion.

const GOLD = '#E4C766'
const GOLD_500 = '#C9A227'
const RING = 26
const LERP = 0.5 // tight follow so the ring sits on the cursor, not trailing behind it

const INTERACTIVE =
  'a, button, [role="button"], input, textarea, select, .sov-detail-close, [tabindex]:not([tabindex="-1"])'

export default function Cursor() {
  const ringRef = useRef<HTMLDivElement | null>(null)
  const raf = useRef<number | null>(null)
  const target = useRef({ x: -100, y: -100 })
  const pos = useRef({ x: -100, y: -100 })
  const hover = useRef(false)
  const shown = useRef(false)

  const enabled =
    typeof window !== 'undefined' &&
    window.matchMedia('(pointer: fine)').matches &&
    !window.matchMedia('(pointer: coarse)').matches

  useEffect(() => {
    if (!enabled) return
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const isInteractive = (el: Element | null): boolean =>
      !!el && (!!el.closest(INTERACTIVE) || getComputedStyle(el).cursor === 'pointer')

    const paint = (x: number, y: number) => {
      const r = ringRef.current
      if (!r) return
      const s = hover.current ? 1.55 : 1
      r.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%) scale(${s})`
      r.style.background = hover.current ? 'rgba(228,199,102,0.16)' : 'transparent'
      r.style.borderColor = hover.current ? GOLD : 'rgba(228,199,102,0.8)'
    }

    const onMove = (e: PointerEvent) => {
      target.current.x = e.clientX
      target.current.y = e.clientY
      hover.current = isInteractive(e.target instanceof Element ? e.target : null)
      if (!shown.current) {
        shown.current = true
        if (ringRef.current) ringRef.current.style.opacity = '1'
        pos.current.x = e.clientX
        pos.current.y = e.clientY
      }
      if (reduce) {
        pos.current.x = e.clientX
        pos.current.y = e.clientY
        paint(e.clientX, e.clientY)
      }
    }

    const tick = () => {
      pos.current.x += (target.current.x - pos.current.x) * LERP
      pos.current.y += (target.current.y - pos.current.y) * LERP
      paint(pos.current.x, pos.current.y)
      raf.current = requestAnimationFrame(tick)
    }

    window.addEventListener('pointermove', onMove, { passive: true })
    if (!reduce) raf.current = requestAnimationFrame(tick)
    return () => {
      window.removeEventListener('pointermove', onMove)
      if (raf.current !== null) cancelAnimationFrame(raf.current)
    }
  }, [enabled])

  if (!enabled) return null

  const style: CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: RING,
    height: RING,
    borderRadius: '50%',
    border: `1.5px solid rgba(228,199,102,0.8)`,
    boxShadow: `0 0 10px ${GOLD_500}55`,
    opacity: 0,
    pointerEvents: 'none',
    zIndex: 60,
    transform: 'translate3d(-100px, -100px, 0)',
    transition: 'background 160ms ease, border-color 160ms ease, opacity 300ms ease',
    willChange: 'transform',
  }

  return <div ref={ringRef} aria-hidden="true" style={style} />
}
